import React, { useEffect, useMemo, useState } from 'react';
import { Countdown, SeatmapSelect, SitePolicy } from '@arema/components';
import { SeatmapSeat, SectionSeatmap, SectionStatus, SetLoading } from '@arema/components/Classes';
import { addCommas, bindClose, formatSeatNumber, getDateStatusLocale } from '@arema/components/Util';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CDN_URL, PLACEHOLDER_IMG, RECAPTCHA, CAPTCHA_ACTIVE } from '../CheckoutConfig';
import { DateData } from '../CheckoutClasses'
import { Icon } from 'semantic-ui-react';
import { firstBy } from 'thenby';
import { Image, Button, Groupper, Header, Message } from 'react-frontier';
import { useTitle } from '@arema/components/Hooks';
import { AccessCodeForm } from '../components';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import classNames from 'classnames';
import Validator from '@arema/components/Validator';
import API from '../CheckoutAPI';
import moment from 'moment';

import '../styles/checkout_seats.scss';

interface CartItem{
	section_id: number,
	section_name: string,
	price_id: number,
	price_name: string,
	seat_id?: number,
	quantity: number,
	cost: number
}

interface CheckoutProps{
	fullscreen?: boolean,
	date?: number
}
var Checkout = (props: CheckoutProps)=>{
	var { setTitle } = useTitle();
	var { t } = useTranslation();
	var params = useParams();
	var navigate = useNavigate();
	var [query, setSearchParams] = useSearchParams();
	var [loadError, setLoadError] = useState<string>(null)
	var [data, setData] = useState<DateData>(null);
	var [cart, setCart] = useState<CartItem[]>(null);
	var [policyModal, setPolicyModal] = useState<boolean>(false);
	var [acceptPolicy, setAcceptPolicy] = useState<boolean>(false);
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [selectedSection, setSelectedSection] = useState<number>(null);
	var [selectedPrice, setSelectedPrice] = useState<number>(null);
	var [selectedDelivery, setSelectedDelivery] = useState<number>(null);
	var [captcha, setCaptcha] = useState<string>(CAPTCHA_ACTIVE ? null : 'CAPTCHA');
	var [seatmapError, setSeatmapError] = useState<string>(null);
	var [seatmap, setSeatmap] = useState<SectionSeatmap>(null);
	var [tokenExpired, setTokenExpired] = useState<boolean>(null);

	var date_id = useMemo(()=>{
		if(props.date){
			if(!Number.isNaN(props.date)){
				return props.date;
			}
		}else{
			if(!Number.isNaN(parseInt(params.date))){
				return parseInt(params.date);
			}
		}
		return null;
	}, [params]);

	var getDateData = (access_token: string=null)=>{
		API.getDate(date_id, query.get('qt'), (access_token || query.get('ac'))).then(res=>{
			if(res.error) return setLoadError(res.message);
			if(res.data.redirect){
				return window.location.href = res.data.redirect;
			}
			setData(res.data);
			if(res.data.event && res.data.event.event_name){
				setTitle(res.data.event.event_name);
			}
		}).catch(err=>{
			return setLoadError(t('common.unexpected_code', { code: 'CHK-DTDTA-1' }));
		});
	}

	useEffect(()=>{
		if(!data && !!date_id){
			getDateData();
		}else{
			if(!data) setTitle(t('checkout.title'));
		}
	}, [data, params, query]);

	useEffect(() => {
		var isInvalidMin = price && cart?.length<price?.min_tickets 
		var isInvalidMultiple = price && ((cart?.length%price?.multiple)!==0);
		var errors = []
		if (isInvalidMin) errors.push(`Es necesario seleccionar mínimo ${price?.min_tickets} boletos`);
		if (isInvalidMultiple) errors.push(`La cantidad de boletos debe de ser un múltiplo de ${price?.multiple}`);
		setErrorPrompts(errors);
	}, [cart])

	if(!date_id) return <Header size='huge' text='404' subtext={t('common.notfound')} />
	if(loadError) return <Header iconName='face-frown-open' text='Error' subtext={loadError} />
	if(!data){
		return <Header loading text={t('checkout.loading')} />
	}
	
	var resetSelection = ()=>{
		setCart(null);
		setSelectedDelivery(null);
		setSelectedPrice(null);
		setSelectedSection(null);
		setAcceptPolicy(false);
		setErrorPrompts(null);
	}

	var loadSeatmap = (section_id: number)=>{
		var section = data.sections.find(a=>a.section_id==section_id);
		setCart(null);
		if(!section || !section.numbered) return resetSelection();
		if(seatmap && seatmap.section_id==section_id) return;
		setSeatmapError(null);
		API.getSeatmap(data.date.date_id, section.section_id, query.get('qt')).then(res=>{
			if(res.error) return setSeatmapError(res.message);
			setSeatmap({
				section_id: section.section_id,
				stage_width: section.stage_width,
				stage_height: section.stage_height,
				stage_x: section.stage_x,
				stage_y: section.stage_y,
				seats_height: section.seats_height,
				seats_width: section.seats_width,
				seats: res.data.map((a: any[])=>{
					const seat_cost : number = a[7] ? parseFloat(a[7]) : null;
					const seat_commission : number = a[8] ? parseFloat(a[8]) : null;
					return {
						seat_id: a[0],
						seat_x: a[1],
						seat_y: a[2],
						seat_section: seat_cost ? `$${(seat_cost + seat_commission).toFixed(2)}`: a[3],
						seat_row: a[4],
						seat_number: a[5],
						seat_status: a[6],
						seat_cost: a[7],
					}
				}),
			})
		}).catch(err=>{
			return setSeatmapError(t('common.unexpected_code', { code: 'CHK-STMP-1' }));
		})
	}

	var showSection = (section_id: number)=>{
		return ()=>{
			setAcceptPolicy(false);
			setCart(null);
			setSelectedPrice(null);
			setSelectedDelivery(null);

			var section = data.sections.find(a=>a.section_id==section_id);
			if(!section) return;
			if(section.numbered){
				if(section.prices.length==1){
					setSelectedPrice(section.prices[0].price_id);
					loadSeatmap(section_id);
				}
			}
			setSelectedSection(section_id);
		}
	}

	var addTicket = (section_id: number, price_id: number, remove: boolean=false)=>{
		return ()=>{
			var ct = cart ? [...cart] : [];
			var cx = ct.findIndex(a=>a.price_id==price_id && a.section_id==section_id);
			var section = data.sections.find(a=>a.section_id==section_id);
			if(!section) return;
			var price = section.prices.find(a=>a.price_id==price_id);
			if(!price) return;
			
			var max_tickets = price.max_tickets%price.multiple!==0 ? price.max_tickets-(price.max_tickets%price.multiple) : price.max_tickets;
			if(cx==-1 && !remove){
				ct.push({
					section_id: section.section_id,
					section_name: section.section_name,
					price_id: price.price_id,
					price_name: price.price_name,
					quantity: 0,
					cost: price.cost+price.commission,
				});
				cx = ct.length-1;
			}
			
			ct[cx].quantity = Math.min(max_tickets, Math.max(0, ct[cx].quantity+((remove ? -1 : 1)*price.multiple)));
			setCart(ct);
		}
	}

	var submit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator({
			cart,
			delivery: selectedDelivery,
			accept: acceptPolicy,
			captcha
		}, {
			cart: [{
				rule: (val: any)=>{
					if(!val || val.length==0) return false;
					var cart_items = (val as any[]).reduce((a,b)=>a+Math.max(0, b.quantity), 0);
					return cart_items>0;
				},
				prompt: t('checkout.form.cart'),
			}],
			delivery: [
				{ rule: 'number', prompt: t('checkout.form.delivery') },
			],
			accept: [
				{ rule: 'empty', prompt: t('checkout.form.accept') },
			],
			captcha: [
				{ rule: 'empty', prompt: t('checkout.form.captcha') },
			]
		});
		setErrorPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.createOrder(cart.map(a=>({
			seat_id: a.seat_id || null,
			price_id: a.price_id,
			quantity: a.quantity,
		})), selectedDelivery, captcha, query.get('qt')).then(res=>{
			if(res.error){
				var prompts = [res.message];
				if(seatmap && seatmap.seats && res.data && res.data.unavailable_seats && res.data.unavailable_seats.length>0){
					var seats = [];
					for(var i of res.data.unavailable_seats){
						var st = seatmap.seats.find(a=>a.seat_id==i);
						if(!st) continue;
						seats.push(formatSeatNumber(st.seat_row, st.seat_number));
					}
					if(seats.length==res.data.unavailable_seats.length){
						prompts.push(`${t('checkout.form.unavailable_seats')}: ${seats.join(', ')}`)
					}
				}
				return setErrorPrompts(prompts);
			}
			if(!res.data || !res.data.token){
				return setErrorPrompts([t('common.unexpected_code', { code: 'CHK-CRTOR-1' })]);
			}
			navigate(`/c/${res.data.token}`);
		}).catch(err=>{
			return setErrorPrompts([t('common.unexpected_code', { code: 'CHK-CRTOR-2' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var onSeatsSelected = (seats: SeatmapSeat[])=>{
		if(!selectedSection || !selectedPrice) return;
		var section = data.sections.find(a=>a.section_id==selectedSection);
		if(!section) return;
		var price = section.prices.find(a=>a.price_id==selectedPrice);
		if(!price) return;
		if(seats.length>price.max_tickets) return; 

		var new_cart = seats.map(a=>({
			section_id: section.section_id,
			section_name: section.section_name,
			price_id: price.price_id,
			price_name: price.price_name,
			seat_id: a.seat_id,
			quantity: 1,
			cost: a.seat_cost || (price.cost+price.commission),
		}))
		setCart(new_cart);
		return true;
	}

	var restartQueue = ()=>{
		query.delete('qt');
		setSearchParams(query);
		setData(null);
	}

	var onTokenExpired = ()=>{
		setTokenExpired(true);
	}

	var onAccessToken = (token: string)=>{
		if(!token) return;
		query.set('ac', token);
		setSearchParams(query);
		setData(null);
		// getDateData(token);
	}

	if(tokenExpired){
		return <Header 
			iconName='exclamation-circle' 
			text={t('checkout.expired.title')}
			subtext={t('checkout.expired.text')}
			subheaderStyle={{ fontSize: 18 }} 
			actions={(
				<Button text={t('checkout.expired.button')} color='black' onClick={restartQueue} style={{ width: 200, padding: 5 }} />
			)}
		/>
	}

	var SelectSection =  !!data.sections && <>
		<div className="ar section label">{t('checkout.section.title')}</div>
		<div className="ar sections" style={{ minHeight: 400 }}>
			{data.sections.sort(firstBy('status', 'asc')).map(a=>{
				return <div 
					key={`sect-item-${a.section_id}`} 
					className={classNames('section', {
						soldout: a.status===SectionStatus.SOLD_OUT
					})} 
					onClick={a.status===SectionStatus.AVAILABLE ? showSection(a.section_id) : null}
				>
					<div className="price">
						{a.status===SectionStatus.SOLD_OUT ? (
							t('checkout.section.sold_out')
						) : <>
							${addCommas(a.prices_min, true, true)}
							{a.prices_min!==a.prices_max ? (
								<> - ${addCommas(a.prices_max, true, true)}</>
							) : null}
						</>}
					</div>
					<div className="name">{a.section_name}</div>
				</div>
			})}
		</div>
	</>

	var cart_items = 0, tickets_total = 0, delivery_cost = 0;
	if(cart){
		for(var i of cart){
			cart_items += i.quantity || 0;
			tickets_total += (i.cost || 0)*(i.quantity || 0);
		}
	}
	if(selectedDelivery){
		var dl = data.delivery.find(a=>a.delivery_id==selectedDelivery);
		if(dl) delivery_cost = dl.delivery_cost;
	}

	var section = (selectedSection && data.sections) ? data.sections.find(a=>a.section_id===selectedSection) : null;
	var price = section?.prices.find(a=>a.price_id==selectedPrice);
	var isInvalidMin = price && cart?.length<price?.min_tickets 
	var isInvalidMultiple = price && ((cart?.length%price?.multiple)!==0);

	var SectionPrices = (!section || section.numbered) ? null : <div style={{ minHeight: cart_items>0 ? 0 : 400 }}>
		<div className="ar section label">{t('checkout.section.selected_section')}</div>
		<div className="ar sections">
			<div className="section placeholder">
				<div className="name">{section.section_name}</div>
			</div>
		</div>
		<div className="ar section label" style={{ marginTop: 10 }}>{t('checkout.section.tickets_title')}</div>
		<div className="ar prices">
			{section.prices.map(a=>{
				var quantity = 0;
				if(cart){
					var c = cart.find(c=>c.price_id==a.price_id && c.section_id==section.section_id);
					if(c) quantity = c.quantity;
				}

				return <div className="price" key={`sect${section.section_id}-pr${a.price_id}`}>
					<div className="cost">${addCommas(a.cost+a.commission, true, true)}</div>
					<div className="name">{a.price_name}</div>
					<div className="quantity">
						<Button className='minus' color='black' iconName="minus" onClick={addTicket(section.section_id, a.price_id, true)} />
						<div className="amount">{quantity}</div>
						<Button className='plus' color='black' iconName="plus" onClick={addTicket(section.section_id, a.price_id)} />
					</div>
				</div>
			})}
		</div>
	</div>

	var SectionNumbered = !section || !section.numbered ? null : (
		selectedPrice ? (
			<SeatmapSelect seatmap={seatmap} error={seatmapError} onSelected={onSeatsSelected} loading={!seatmap} selected={cart ? cart.filter(a=>!!a.seat_id).map(a=>a.seat_id) : []} />
		) : (
			<div>
				<div style={{ minHeight: 350 }}>
					<div className="ar section label">{t('checkout.section.selected_section')}</div>
					<div className="ar sections">
						<div className="section placeholder">
							<div className="name">{section.section_name}</div>
						</div>
					</div>
					<div className="ar section label" style={{ marginTop: 10 }}>{t('checkout.seatmap.price_title')}</div>
					<div className="ar selectable prices">
						{section.prices.length==0 ? (
							<Message text='' header={t('common.attention')} centered type='error' />
						) : section.prices.map(a=>(
							<div className="price" key={`sect${section.section_id}-pr${a.price_id}`} onClick={()=>{
								setSelectedPrice(a.price_id);
								loadSeatmap(selectedSection);
							}}>
								<div className="cost">
									${addCommas(a.cost+a.commission, true, true)}
								</div>
								<div className="name">{a.price_name}</div>
							</div>
						))}
					</div>
				</div>
				{section.prices.length>0 ? (
					<Message centered type='info' text={t('checkout.seatmap.select_seats')} />
				) : null}
			</div>
		)
	)

	return <div className="ar checkout-contents">
		{!props.fullscreen ? (
			<div className="ar event">
				<Image src={`${CDN_URL}/events/${data.event.event_id}/200.webp`} fallback={PLACEHOLDER_IMG} alt={data.event.event_name} />
				<div className="info">
					<div className="name">{data.event.event_name}</div>
					<div className="date">{moment.unix(data.date.date).format('DD/MMM/YY HH:mm')}</div>
					<div className="venue">{data.date.venue_name}</div>
					<div className="city">{data.date.city}</div>
				</div>
			</div>
		) : (
			<div style={{ height: 15 }} />
		)}
		{!!data && !!data.expiration && (
			<Message centered className='queue' type='info'>
				<div className="content">
					<Header text={t('checkout.queue.title')} size='small' centered={false} />
					<Countdown unix={data.expiration} className='countdown' onFinish={onTokenExpired} />
					{t('checkout.queue.text')}
					<div style={{ fontSize: 12, lineHeight: '12px', marginTop: 5 }}>{t('checkout.queue.turn')}: <b>{data.order_id.toString(18).toUpperCase()}</b></div>
				</div>
			</Message>
		)}
		{data.access_code ? (
			<AccessCodeForm eventId={data.event.event_id} onToken={onAccessToken} />
		) : (
			<Groupper titleCentered
				className={classNames('selection', {
					numbered: section && selectedPrice && section.numbered,
					fitted: section && selectedPrice && section.numbered,
					fullscreen: props.fullscreen,
				})} 
				style={{ maxWidth: (section?.numbered && selectedPrice) ? 1000 : 500 }}
				title={<>
					{t('checkout.title')}
					{!!props.fullscreen && (
						<div className="sub header">
							{moment.unix(data.date.date).format('DD/MMM/YY HH:mm')}
						</div>
					)}
				</>}
			>
				{!data.date.available ? (
					<div className='unavailable'>
						{/* TODO: Convert to locale */}
						<Header text={getDateStatusLocale(data.date.status_id)} subtext={t('checkout.unavailable')} iconName='face-frown-open' />
					</div>
				) : <>
					{!selectedSection ? (
						SelectSection
					) : section ? (
						!section.numbered ? SectionPrices : SectionNumbered
					) : (
						<Header size='small' text={t('common.error')} subtext={t('checkout.section.notfound')} />
					)}
					{cart_items>0 ? (
						<div style={{ marginTop: section && section.numbered ? 0 : 30 }}>
							<div className="ar section label">{t('checkout.delivery.title')}</div>
							{data.delivery.length>0 ? (
								<div className="ar delivery">
									{data.delivery.map(a=>(
										<div className={classNames('method', {
											active: selectedDelivery==a.delivery_id
										})} key={`dlv-${a.delivery_id}`} onClick={()=>setSelectedDelivery(a.delivery_id)}>
											<Icon name='check circle' />
											<div className="name">
												{a.needs_location ? (
													<Icon name="truck" style={{ marginRight: 10 }} />
												) : null}
												{a.method_name}
											</div>
											<div className="cost">{a.delivery_cost>0 ? `$${addCommas(a.delivery_cost, true, true)}` : t('common.free')}</div>
										</div>
									))}
								</div>	
							) : (
								<Header text={t('common.error')} subtext={`${t('checkout.delivery.empty')} (DTND-${data.date.date_id})`} />
							)}
						</div>
					) : null}
					{!!selectedSection && cart_items>0 && !!selectedDelivery && (
						<div className={classNames('ar terms checkbox', {
							active: acceptPolicy
						})} style={{ marginTop: 10 }} onClick={()=>setAcceptPolicy(!acceptPolicy)}>
							<i className="icon"></i>
							<div className="label">
								<div className="text">{t('checkout.delivery.policy')}</div>
							</div>
						</div>
					)}

					{selectedDelivery && cart_items>0 && CAPTCHA_ACTIVE ? (
						<div className="ar captcha">
							<ReCAPTCHA 
								hl='es-419'
								sitekey={RECAPTCHA}
								onChange={setCaptcha}
								onExpired={()=>setCaptcha(null)}
							/>
						</div>
					) : null}
					{cart_items>0 ? (
						<table className="totals">
							<tbody>
								{section && section.numbered ? (
									<tr>
										<td>{t('checkout.totals.seats')}</td>
										<td>{addCommas(cart.length, false)}</td>
									</tr>
								) : null}
								<tr>
									<td>{t('checkout.totals.tickets')}</td>
									<td className="tickets">${addCommas(tickets_total)}</td>
								</tr>
								<tr>
									<td>{t('checkout.totals.delivery')}</td>
									<td className="delivery">
										{selectedDelivery ? (
											'$'+addCommas(delivery_cost)
										) : (
											<Icon name='minus' />
										)}
									</td>
								</tr>
								<tr className="subtotal">
									<td>{t('checkout.totals.subtotal')}</td>
									<td className="total">${addCommas(tickets_total+delivery_cost)}</td>
								</tr>
							</tbody>
						</table>
					) : null}
					<Message list={errorPrompts} type='error' style={{ marginTop: 10 }} />
					{selectedSection ? <>
						<div className="actions" style={{ display: 'flex', justifyContent: 'center' }}>
							<Button basic text={t('common.return')} onClick={resetSelection} style={{ marginRight: 10, minWidth: 150, fontSize: 14 }} />
							<Button text={t('common.continue')} color='black' disabled={cart_items<=0 || !selectedDelivery || isInvalidMin || isInvalidMultiple} onClick={submit} />
						</div>
					</> : null}
				</>}
			</Groupper>
		)}
		<div style={{ justifyContent: 'center', display: 'flex', width: '100%', marginTop: 15, marginBottom: 15 }}>
			<Button basic text={t('checkout.footer.policy')} onClick={bindClose(setPolicyModal, true)} />
			<Button basic text={t('checkout.footer.privacy')} as={Link} to={'/privacidad'} target='_blank' />
		</div>
		<SitePolicy open={policyModal} onClose={bindClose(setPolicyModal)} />
	</div>
}

export default Checkout;